import { render } from 'preact';
import { BASE_HERO_SLOTS, type AccountView } from '@knight/shared';
import { App } from './ui/App';
import * as S from './app/state';
import { detectLang, setLang, t } from './i18n';
import { api, getToken, setToken } from './net/api';
import { game } from './game/client';
import { audio } from './game/audio';
import { loadLocalPets } from './app/pets';
import { loadLocalHeroes, loadLocalHeroBest } from './app/heroes';
import { installBrowserGuards } from './app/guards';
import { installDeviceWatch } from './app/device';

function localAccount(): AccountView {
  const name = localStorage.getItem('knight.name') || 'Wanderer';
  return {
    id: 'local',
    name,
    avatar: 0,
    fame: 0,
    rating: 1000,
    isGuest: true,
    lang: 'en',
    createdAt: Date.now(),
    stats: { runs: 0, kills: 0, bestLevel: 0, bossKills: 0, revenantKills: 0, duelWins: 0, duelLosses: 0, revenantsSlainOfMine: 0, referrals: 0 },
    guild: null,
    titles: [],
    favoriteHero: null,
    pets: loadLocalPets(),
    heroes: loadLocalHeroes(),
    heroSlots: BASE_HERO_SLOTS,
    heroBest: loadLocalHeroBest(),
    vault: [],
    vaultSlots: 12,
    visibility: 'public',
    showMode: false,
  };
}

function parseLinks(): { ref: string | null } {
  const url = new URL(location.href);
  const ref = url.searchParams.get('ref');
  const hash = location.hash;
  const m = /^#\/(s|r|g|d|w)\/([^/?#]+)/.exec(hash);
  if (m) {
    const kind = m[1];
    const val = decodeURIComponent(m[2]);
    if (kind === 's' || kind === 'd') S.pendingSquad.value = val.toUpperCase();
    if (kind === 'r') S.pendingRealm.value = val.toUpperCase();
    if (kind === 'g') S.pendingAvenge.value = val;
    if (kind === 'w') S.pendingWatch.value = val.slice(0, 16);
  }
  if (m || ref) history.replaceState(null, '', location.pathname + (url.searchParams.has('tab') ? '?tab=1' : '') + (url.searchParams.has('offline') ? (url.searchParams.has('tab') ? '&' : '?') + 'offline=1' : ''));
  return { ref };
}

async function auth(ref: string | null): Promise<boolean> {
  const lang = detectLang();
  try {
    if (getToken()) {
      try {
        S.account.value = await api.me();
        return true;
      } catch (e) {
        if ((e as { status?: number }).status !== 401) throw e;
        setToken(null);
      }
    }
    const r = await api.guest(lang, ref);
    setToken(r.token);
    S.account.value = r.account;
    if (ref) S.showToast(t('toast.referral'));
    return true;
  } catch {
    S.account.value = localAccount();
    return false;
  }
}

async function boot() {
  await setLang(detectLang());
  const { ref } = parseLinks();
  render(<App />, document.getElementById('ui')!);
  installGlobalListeners();
  const ok = await auth(ref);
  game.onAuthError = async () => {
    setToken(null);
    await auth(null);
    game.rehello();
  };
  await game.connect();
  if (!ok) S.conn.value = 'offline';
  // follow deep links once connected
  const applyLinks = () => {
    if (S.pendingSquad.value && S.conn.value === 'online') {
      game.send({ t: 'squad.join', code: S.pendingSquad.value });
      S.pendingSquad.value = null;
    }
    if (S.pendingRealm.value || S.pendingAvenge.value) {
      if (S.conn.value === 'online') S.screen.value = 'heroes';
      else if (S.conn.value === 'offline') {
        S.pendingRealm.value = null;
        S.pendingAvenge.value = null;
      }
    }
    if (S.pendingWatch.value) {
      if (S.conn.value === 'online') {
        const name = S.pendingWatch.value;
        S.pendingWatch.value = null;
        game.watch(name);
      } else if (S.conn.value === 'offline') {
        S.pendingWatch.value = null;
        S.showToast(t('toast.needServer'));
      }
    }
  };
  applyLinks();
  const unsub = S.conn.subscribe((c) => {
    if (c === 'online') applyLinks();
  });
  void unsub;
  audio.music('menu');
}

/** Listeners that must exist even while the network is slow or down. */
function installGlobalListeners(): void {
  installBrowserGuards();
  installDeviceWatch();
  window.addEventListener('pointerdown', () => audio.unlock(), { once: true });
  window.addEventListener('keydown', () => audio.unlock(), { once: true });
  // owner debug console
  window.addEventListener('keydown', (e) => {
    if (e.code === 'F8') {
      e.preventDefault();
      S.adminOpen.value = !S.adminOpen.value;
    }
  });
}

(window as unknown as { knight: unknown }).knight = { game, state: S, audio };
void boot();
